import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "./Brand";
import { initials, isAccountant, isAdmin, roleLabel } from "@/lib/utils";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Search as SearchIcon, X as CloseIcon, ShieldAlert, Plug } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { ChatDockProvider, ChatDockLauncher, ChatDock } from "./ChatDock";
import { BugReportButton } from "./BugReportButton";
import { Tutorial } from "./Tutorial";
import { PwaInstallPrompt } from "./PwaInstallPrompt";
import { EnablePushBanner } from "./EnablePushBanner";
import { GlobalSearch } from "./GlobalSearch";
import { ShortcutHelp } from "./ShortcutHelp";
import { HandbookSignoff } from "./HandbookSignoff";
import { HelpButton } from "./HelpButton";
import { useThemeSync } from "@/hooks/useTheme";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import type { Role } from "@/types/db";
import { hasPermission, type PermissionKey } from "@/lib/permissions";
import {
  LayoutDashboard, Megaphone, Clock, Plane, KanbanSquare, Target, ClipboardList, GraduationCap,
  ShoppingCart, Ticket, MessageSquare, PartyPopper, Inbox, Users, BookOpen, MessageCircle, User,
  FolderOpen, Search, ClipboardCheck, LogOut, Menu, Compass, Briefcase, Award, Video, Wrench,
  Handshake, Contact, Workflow, CalendarDays,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ADMIN_GROUPS, canSeeNavItem, type NavItem, type NavGroup } from "@/lib/admin-nav";


// Standalone top-level links (rendered outside any drawer).
const DASHBOARD: NavItem = { to: "/", label: "Dashboard", Icon: LayoutDashboard };
const PROFILE: NavItem = { to: "/profile", label: "My Profile", Icon: User };

// Personal nav: HQ's six jobs, one drawer each (HQ strip-down, 2026-10).
// Admin items live in the same six sections under Admin (lib/admin-nav.ts).
const NAV_GROUPS: NavGroup[] = [
  { title: "Communication", Icon: MessageCircle, items: [
    { to: "/announcements", label: "Announcements", Icon: Megaphone, allow: ["employee", "admin", "super_admin", "accountant"] },
    { to: "/chat", label: "Chat", Icon: MessageCircle, allow: ["employee", "admin", "super_admin"] },
    { to: "/huddle", label: "Video Huddle", Icon: Video, allow: ["employee", "admin", "super_admin", "accountant"] },
    { to: "/kudos", label: "Kudos", Icon: PartyPopper, allow: ["employee", "admin", "super_admin"] },
    { to: "/wins", label: "Daily Wins", Icon: Award, allow: ["employee", "admin", "super_admin"] },
    { to: "/polls", label: "Polls", Icon: Inbox, allow: ["employee", "admin", "super_admin"] },
    { to: "/suggestions", label: "Suggestion Box", Icon: Inbox, allow: ["employee", "manager", "admin", "super_admin", "c_suite", "accountant", "sales_rep"] },
    { to: "/dev-requests", label: "Core Dev Requests", Icon: Wrench, allow: ["employee", "manager", "admin", "super_admin", "c_suite", "accountant", "sales_rep"] },
    // Mary Ann's queue; gated on can_access_support, not on role.
    { to: "/support", label: "Support Tickets", Icon: Ticket, allow: ["employee", "admin", "super_admin"], gated: "support" },
  ] },
  { title: "Organization", Icon: Users, items: [
    { to: "/directory", label: "Directory", Icon: Users, allow: ["employee", "admin", "super_admin"] },
    { to: "/mission", label: "Mission & Goals", Icon: Compass, allow: ["employee", "admin", "super_admin", "accountant"] },
    { to: "/jobs", label: "Job Board", Icon: Briefcase, allow: ["employee", "admin", "super_admin"] },
    { to: "/new-hires", label: "New hires", Icon: Users, allow: ["manager", "admin", "super_admin"] },
    { to: "/reviews", label: "My Reviews", Icon: ClipboardList, allow: ["employee", "admin", "super_admin"] },
    { to: "/goals", label: "My Goals", Icon: Target, allow: ["employee", "admin", "super_admin"] },
    { to: "/one-on-ones", label: "1:1 Notes", Icon: MessageSquare, allow: ["admin", "super_admin"] },
    { to: "/orders", label: "Order Requests", Icon: ShoppingCart },
  ] },
  { title: "Time Clock", Icon: Clock, items: [
    { to: "/time", label: "Time Tracking", Icon: Clock },
    { to: "/pto", label: "Time Off", Icon: Plane, allow: ["employee", "admin", "super_admin"] },
  ] },
  { title: "SOP Library", Icon: BookOpen, items: [
    { to: "/sops", label: "SOPs", Icon: BookOpen, allow: ["employee", "admin", "super_admin"] },
    { to: "/training", label: "Training", Icon: GraduationCap, allow: ["employee", "admin", "super_admin"] },
    { to: "/checklists", label: "Checklists", Icon: ClipboardCheck, allow: ["employee", "admin", "super_admin"] },
    { to: "/handbook", label: "Handbook", Icon: BookOpen, allow: ["employee", "admin", "super_admin"] },
  ] },
  { title: "Document Library", Icon: FolderOpen, items: [
    { to: "/documents", label: "Documents", Icon: FolderOpen, allow: ["employee", "admin", "super_admin"] },
  ] },
  { title: "CRM", Icon: Handshake, items: [
    { to: "/crm/contacts", label: "Contacts", Icon: Contact, allow: ["employee", "manager", "admin", "super_admin", "c_suite", "sales_rep"] },
    { to: "/crm/pipelines", label: "Pipelines", Icon: Workflow, allow: ["employee", "manager", "admin", "super_admin", "c_suite", "sales_rep"] },
    { to: "/crm/calendar", label: "Calendar", Icon: CalendarDays, allow: ["employee", "manager", "admin", "super_admin", "c_suite", "sales_rep"] },
  ] },
  { title: "Team Board", Icon: KanbanSquare, items: [
    { to: "/tasks", label: "Boards", Icon: KanbanSquare, allow: ["employee", "admin", "super_admin"] },
  ] },
];

const ACCOUNTANT_NAV: NavItem[] = [
  { to: "/accountant/employees", label: "Employee Roster", Icon: Users },
  { to: "/accountant/time", label: "Time Logs", Icon: Clock },
];


export function Layout() {
  const { employee, signOut } = useAuth();
  useThemeSync(employee);
  useKeyboardShortcuts();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const role = employee?.role;

  // Explicitly (re)start the runtime translator when the Layout mounts and
  // whenever the language preference changes. Belt-and-suspenders on top
  // of the module-init boot, because module init can happen before React
  // has committed the sidebar.
  useEffect(() => {
    const lang = employee?.language_preference || localStorage.getItem("scenta-hq-lang") || "en";
    void import("@/lib/autoTranslate").then(m => m.startAutoTranslate(lang));
  }, [employee?.language_preference]);

  async function onLogout() {
    await signOut();
    nav("/login");
  }

  // Can the signed-in employee see this nav item?
  const canSee = (i: NavItem): boolean => canSeeNavItem(i, employee);
  // Filter each group's items, drop groups that end up empty.
  const filterGroups = (groups: NavGroup[]) =>
    groups.map(g => ({ ...g, items: g.items.filter(canSee) })).filter(g => g.items.length > 0);
  const personalGroups = filterGroups(NAV_GROUPS);
  const adminGroups = filterGroups(ADMIN_GROUPS);
  const adminSectionTitle = isAdmin(role) ? "Admin" : "Manage";
  const acctNav = isAccountant(role) ? ACCOUNTANT_NAV : [];

  return (
    <ChatDockProvider>
    <div className="flex min-h-screen bg-offwhite">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-line bg-white transition-transform md:relative md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 items-center border-b border-line px-5">
          <Link to="/" onClick={() => setOpen(false)} className="block"><Brand /></Link>
        </div>
        <nav className="flex h-[calc(100vh-4rem)] flex-col">
          <SidebarNav
            dashboard={DASHBOARD}
            profile={PROFILE}
            personalGroups={personalGroups}
            adminGroups={adminGroups}
            adminTitle={adminSectionTitle}
            accountant={acctNav}
            onNavClick={() => setOpen(false)}
          />
          <div className="border-t border-line p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-mint font-semibold text-teal-dark">
                {employee && initials(employee.first_name, employee.last_name, employee.email)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{employee?.first_name || employee?.email}</div>
                <div className="truncate text-xs text-gray2">{role && roleLabel(role)}</div>
              </div>
              <button onClick={onLogout} className="text-gray2 hover:text-bad" title="Log out">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-ink/60 md:hidden" onClick={() => setOpen(false)} />}

      <div className="flex flex-1 flex-col min-w-0">
        <header className="flex h-16 items-center justify-between border-b border-line bg-white px-5">
          <div className="flex items-center gap-3 md:hidden">
            <button onClick={() => setOpen(true)} className="text-teal-dark"><Menu size={26} /></button>
            <Brand />
          </div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-1">
            <HelpButton />
            <ChatDockLauncher />
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 md:p-8">
          <EnablePushBanner />
          <Outlet />
        </main>
      </div>
      <BugReportButton />
      <PwaInstallPrompt />
      <Tutorial />
      <GlobalSearch />
      <ShortcutHelp />
      <HandbookSignoff />
      <ChatDock />
    </div>
    </ChatDockProvider>
  );
}

// ── Sidebar: search + main menus with collapsible sub-drawers ─────────────
// Top-level links (Dashboard, My Profile) sit outside any drawer. Everything
// else is organized into main menus that expand into sub-menu drawers. Each
// drawer's open/closed state persists in localStorage. A drawer is forced open
// when it contains the active route or when a search query matches an item in
// it, so results are always visible without an extra click.
const COLLAPSE_KEY = "scenta-hq-sidebar-drawers";
function readCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function writeCollapsed(state: Record<string, boolean>) {
  try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

// Does `pathname` fall under this nav item? "/" only matches exactly; others
// match the path or any of its sub-routes.
function isItemActive(to: string, pathname: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

function ItemLink({ item, onNavClick }: { item: NavItem; onNavClick: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavClick}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
          isActive ? "bg-teal-mint text-teal-dark" : "text-charcoal hover:bg-offwhite"
        }`
      }
    >
      <item.Icon size={18} className="shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function SidebarNav({ dashboard, profile, personalGroups, adminGroups, adminTitle, accountant, onNavClick }: {
  dashboard: NavItem; profile: NavItem;
  personalGroups: NavGroup[]; adminGroups: NavGroup[]; adminTitle: string;
  accountant: NavItem[]; onNavClick: () => void;
}) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed);
  useEffect(() => { writeCollapsed(collapsed); }, [collapsed]);
  const { pathname } = useLocation();

  const q = query.trim().toLowerCase();

  // Prepare a rendered list of drawers for a set of groups, prefixing collapse
  // keys so personal/admin groups with the same title never collide. When
  // searching, only keep matching items and drop groups with no matches.
  function drawersFor(groups: NavGroup[], prefix: string) {
    return groups
      .map(g => {
        const items = q ? g.items.filter(i => i.label.toLowerCase().includes(q)) : g.items;
        return { key: `${prefix}:${g.title}`, group: g, items };
      })
      .filter(d => d.items.length > 0);
  }
  const personalDrawers = drawersFor(personalGroups, "p");
  const adminDrawers = drawersFor(adminGroups, "a");
  const acctItems = q ? accountant.filter(i => i.label.toLowerCase().includes(q)) : accountant;

  const dashMatch = !q || dashboard.label.toLowerCase().includes(q);
  const profileMatch = !q || profile.label.toLowerCase().includes(q);
  const nothing = !dashMatch && !profileMatch && personalDrawers.length === 0 &&
    adminDrawers.length === 0 && acctItems.length === 0;

  function Drawer({ dkey, group, items }: { dkey: string; group: NavGroup; items: NavItem[] }) {
    const hasActive = items.some(i => isItemActive(i.to, pathname));
    // Auto-open the drawer holding the active route (and on search), but an
    // explicit user toggle always wins — so the drawer you're in can still close.
    const userSet = Object.prototype.hasOwnProperty.call(collapsed, dkey);
    const openNoSearch = userSet ? !collapsed[dkey] : hasActive;
    const isOpen = !!q || openNoSearch;
    return (
      <div className="mb-1">
        <button
          onClick={() => setCollapsed(s => ({ ...s, [dkey]: openNoSearch }))}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition ${
            hasActive ? "text-teal-dark" : "text-charcoal hover:bg-offwhite"
          }`}
        >
          <group.Icon size={18} className="shrink-0" />
          <span className="flex-1 truncate text-left">{group.title}</span>
          {isOpen ? <ChevronDown size={14} className="text-gray2" /> : <ChevronRight size={14} className="text-gray2" />}
        </button>
        {isOpen && (
          <div className="ml-3 mt-0.5 space-y-0.5 border-l border-line pl-2">
            {items.map(i => <ItemLink key={i.to} item={i} onNavClick={onNavClick} />)}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="border-b border-line px-3 py-2.5">
        <div className="relative">
          <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray2 pointer-events-none" />
          <input
            type="text"
            name="sidebar-nav-search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-form-type="other"
            data-1p-ignore
            data-lpignore="true"
            className="w-full rounded-lg bg-offwhite pl-8 pr-8 py-2 text-sm placeholder:text-gray2 focus:outline-none focus:ring-2 focus:ring-teal"
            placeholder="Search menu…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") setQuery(""); }}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray2 hover:text-bad"
              title="Clear"
            >
              <CloseIcon size={14} />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {nothing && (
          <div className="text-center text-xs text-gray2 py-6">No menu items match.</div>
        )}

        {dashMatch && <div className="mb-1"><ItemLink item={dashboard} onNavClick={onNavClick} /></div>}

        {personalDrawers.map(d => <Drawer key={d.key} dkey={d.key} group={d.group} items={d.items} />)}

        {profileMatch && <div className="mb-1 mt-1"><ItemLink item={profile} onNavClick={onNavClick} /></div>}

        {adminDrawers.length > 0 && (
          <>
            <div className="mt-4 mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-gray2">{adminTitle}</div>
            {adminDrawers.map(d => <Drawer key={d.key} dkey={d.key} group={d.group} items={d.items} />)}
          </>
        )}

        {acctItems.length > 0 && (
          <>
            <div className="mt-4 mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-gray2">Accountant</div>
            <div className="space-y-0.5">
              {acctItems.map(i => <ItemLink key={i.to} item={i} onNavClick={onNavClick} />)}
            </div>
          </>
        )}
      </div>
    </>
  );
}
